import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { findCalculator } from "@/lib/calculators";
import { useT } from "@/lib/i18n";

/**
 * Educational content block: How it works + FAQ + Related tools.
 * Improves SEO & user trust. All content passed via props.
 */
export default function EduSection({ howItWorks, faqs = [], related = [] }) {
  const t = useT();
  return (
    <section className="mt-12 space-y-8" data-testid="edu-section">
      {howItWorks && (
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="overline mb-3">{t("how_it_works")}</div>
          <div className="prose prose-sm max-w-none dark:prose-invert space-y-3 text-sm leading-relaxed text-muted-foreground">
            {howItWorks}
          </div>
        </div>
      )}

      {faqs.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="overline mb-4">{t("faq")}</div>
          <div className="divide-y divide-border">
            {faqs.map((f, i) => <FAQItem key={`${f.q}-${i}`} q={f.q} a={f.a} idx={i} />)}
          </div>
        </div>
      )}

      {related.length > 0 && (
        <div>
          <div className="overline mb-3">{t("related_tools")}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {related.map((slug) => {
              const c = findCalculator(slug);
              if (!c) return null;
              return (
                <Link key={slug} to={`/${slug}`} data-testid={`related-${slug}`} className="p-3 rounded-md border border-border bg-card hover:border-primary/40 transition group flex items-center gap-2">
                  <c.Icon className="w-4 h-4 text-primary" strokeWidth={1.75} />
                  <span className="text-xs font-medium truncate">{c.shortTitle || c.title}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="py-3" data-testid={`faq-item-${idx}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left gap-4">
        <span className="text-sm font-medium">{q}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-2 text-xs sm:text-sm text-muted-foreground leading-relaxed">{a}</div>}
    </div>
  );
}
